import React, { useCallback } from "react";
import { omit } from "lodash";
import queryString from "query-string";
import { useHistory, useParams } from "react-router";
import {
  useEditPropertyMutation,
  useGetEditPropertyQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { Button } from "../Common/Button";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import { SelectedInput } from "../Maps/Map";
import EditPropertyWarnings from "./EditPropertyWarnings";
import PropertyForm, { ActionProps, FormData } from "./PropertyForm";

const EditProperty = () => {
  const history = useHistory();
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { propertyId } = useParams<{ propertyId?: string }>();
  if (!propertyId) return null;

  const { data, loading, error } = useGetEditPropertyQuery({
    variables: { propertyId },
  });

  const [editProperty, { error: updateError, loading: loadingEdit }] =
    useEditPropertyMutation({
      onCompleted: data => {
        const property = data.updateProperty;
        addSuccessToast(`Property edited at ${property.streetAddress}`);
        const query = {
          address: property.standardizedAddress,
          lat: property.latitude,
          lng: property.longitude,
          propertyId: property.id,
          parcelId: property.geocacheParcelId,
        } satisfies SelectedInput;

        history.replace({
          pathname: "/map",
          search: `?${queryString.stringify(query)}`,
        });
      },
      onError: error => {
        const errorMessage = error.graphQLErrors
          .map(error => error.message)
          .join("\n");
        addErrorToast(errorMessage);
      },
    });

  const onSave = useCallback(async (data: FormData) => {
    const newData = {
      ...omit(data, "propertyId"),
      address: data.address ?? null,
      id: propertyId,
      ignoreParcel: data.geocacheParcelId === null,
    };

    await editProperty({ variables: { data: newData } });
  }, []);

  if (loading)
    return (
      <div style={{ textAlign: "center" }}>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </div>
    );

  if (error || !data?.property) return <em>Problem loading edit form.</em>;

  const property = data.property;
  const startingParcel = property.parcel ?? null;

  const startingPoint =
    property.latitude && property.longitude ?
      { latitude: property.latitude, longitude: property.longitude }
    : null;

  const actions = (props: ActionProps): JSX.Element => {
    return (
      <Button
        size="small"
        styleVariant="primary"
        onClick={() => props.validateInput(onSave)}
        disabled={props.disabled || loadingEdit}
      >
        Save
      </Button>
    );
  };

  return (
    <PropertyForm
      headerText="Edit property details"
      title="Edit property details"
      subtitle="Please ensure that the property details below are correct."
      property={property}
      point={startingPoint}
      parcel={startingParcel}
      zoom={null}
      actions={actions}
      error={updateError?.message ?? null}
      warningsComponent={props => {
        return (
          <EditPropertyWarnings
            {...props}
            header={"Property Warnings"}
            showNone={true}
          />
        );
      }}
      canSubmitProperty={true}
      editingProperty={true}
    />
  );
};

export default EditProperty;
